import { stylesheet } from 'astroturf';
import { FunctionalComponent } from 'preact';
import { memo, useRef } from 'preact/compat';
import { getFormattedUnixTimestamp } from '##/utils/DateTimeHelpers';
import { LiveChannel } from '##/services/tenApi/collections/types';
import { useObserver } from '##/utils/hooks';
import LazyImage from '../LazyImage';
import { LiveCarouselItem } from './types';

const styles = stylesheet`
  @import '##/styles/import.scss';

  $poster-height: 88;
  $progress-bar-height: 3;

  .carousel-item {
    position: relative;
    width: pxToVw(166);
    height: pxToVw(231);
    margin-right: pxToVw(20);
    padding: pxToVw(7) pxToVw(7) pxToVw(16);
    color: $pure-white;
    font-family: barlow;
    flex-shrink: 0;
    border: hidden;

    &:global(.current-focus) {
      .carousel-background {
        border-color: white;
        border-radius: pxToVw(4);  
        border-style: solid;
        border-width: pxToVw(2);
        transform: scale(1.1);
      } 

      a {
        color: $pure-black;
      }
    }

    .carousel-background {
      background: transparent linear-gradient(0deg, #13171B 0%, #202020 100%) 0% 0% no-repeat padding-box;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
    }

    .carousel-card-container {
        display: flex;
        flex-direction: column;
        justify-content: space-around;
        align-items: center;
        width: 100%;
        height: 100%;
    }

    .icon {
      object-fit: contain;
      object-position: left;
      max-width: pxToVw(98);
      width: auto;
      height: pxToVw(40);
      margin-bottom: pxToVw(10);
      padding: pxToVw(2);
      z-index: 1;
    }

    .title {
      text-transform: capitalize;
      font-size: pxToVw(16);
      text-overflow: ellipsis;
      line-height: pxToVw(15);
      white-space: nowrap;
      width: pxToVw(140);
      min-height: pxToVh(19);
      overflow: hidden;
      margin-top: pxToVh(8);
      margin-bottom: pxToVh(4);
      text-align: center;
      z-index: 1;
    }

    .time {
      font-size: pxToVw(15);
      color: $secondary-grey;
      margin: pxToVh(2) 0 0 0;
      z-index: 1;
    }

    .live-show-rating {
      @extend .time;

      margin-top: pxToVh(6);
    }

    .progress-bar-back {
      width: 100%;
      height: pxToVw(3);
      bottom: 0;
      left: 0;
      background-color: rgba(233, 233, 233, 0.3);
    }

    .progress-bar {
      height: pxToVw($progress-bar-height);
      bottom: 0;
      left: 0;
    }
    
    .now-playing {
      position: absolute;
      height: 100%;
      display: flex;
      width: 100%;
      justify-content: center;
      align-items: center;
      font-size: pxToVw(18);
      z-index: 1;
    }

    .poster-container {
      position: relative;
      display: flex;
      flex-direction: column;
      min-height: pxToVh($poster-height + $progress-bar-height);
    }

    .poster {
      min-height: pxToVh($poster-height);
      max-height: pxToVh(90);
      max-width: pxToVw(160);
    }

    .playing-poster {
      @extend .poster;

      opacity: 0.2;
    }
  }
  
`;

type LiveChannelWithCurrentPlaying = LiveChannel & {
  liveProgress?: number;
  currentPlaying?: string;
  id?: string;
  domId: string;
};

const OnNowCarouselTile: FunctionalComponent<
  | (LiveCarouselItem & {
      domId: string;
    })
  | LiveChannelWithCurrentPlaying
> = ({ liveShow, currentPlaying, domId, liveProgress, ...restProps }) => {
  const { color, channelKey, logoUrl } = restProps as Partial<
    LiveChannelWithCurrentPlaying
  >;

  const tileRef = useRef<HTMLDivElement>(null);

  const isLiveInbound = useObserver(tileRef, {}, true);

  const { startTime = 0, endTime = 0, rating, ratings } = liveShow || {};

  const currentTime = liveProgress || 0;
  const progressTime = Math.min(
    (currentTime / 1000 - startTime) / (endTime - startTime),
    1,
  );
  const checkCurrentPlaying = currentPlaying === channelKey;

  return (
    <div className={styles.carouselItem} id={domId} ref={tileRef}>
      <div
        className={styles.carouselBackground}
        style={{ borderColor: color }}
      />
      <div className={styles.carouselCardContainer}>
        <LazyImage
          isShowing={isLiveInbound}
          className={styles.icon}
          url={`${logoUrl}?image-profile=foxtel_channel_logo`}
        />
        <div className={styles.posterContainer}>
          {checkCurrentPlaying ? (
            <div className={styles.nowPlaying}>playing</div>
          ) : null}
          <LazyImage
            isShowing={isLiveInbound}
            className={
              checkCurrentPlaying ? styles.playingPoster : styles.poster
            }
            url={`${liveShow?.landscapeImageUrl}?image-profile=foxtel_live_now_card`}
          />
          <div className={styles.progressBarBack}>
            <div
              className={styles.progressBar}
              style={{
                backgroundColor: color,
                width: `calc(100% * ${progressTime})`,
              }}
            />
          </div>
        </div>
        <p className={styles.title}>{liveShow?.title}</p>
        <p className={styles.time}>
          {getFormattedUnixTimestamp(startTime)}
          {' - '}
          {getFormattedUnixTimestamp(endTime)}
        </p>
        <p className={styles.liveShowRating}>{rating || ratings}</p>
      </div>
    </div>
  );
};

export default memo(
  OnNowCarouselTile,
  (prevProps, nextProps) =>
    !(
      prevProps.liveProgress !== nextProps.liveProgress ||
      prevProps.liveShow?.startTime !== nextProps.liveShow?.startTime
    ),
);
